import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST, API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export interface Site {
    lookupValueCd: string;
    lookupValueDesc: string;
}

export interface CheckSuperUserPayload {
    workdayId: string;
    firstName: string;
    lastName: string;
}

export interface CheckSuperUserResponse {
    isSuperUser: boolean;
    isAdmin?: boolean;
}

export interface ValidateUserPayload {
    workdayId: string;
    firstName: string;
    lastName: string;
    siteCd: string;
}

export interface ValidateUserResponse {
    isActive: boolean;
    isAdmin?: boolean;
    isSuperUser?: boolean;
    message?: string;
}

export interface CreateUserPayload {
    siteCd: string;
    workdayId: string;
    firstName: string;
    lastName: string;
    passwordHash: string;
    team: string;
    notes: string;
    email: string;
    phoneNbr?: string | null;
    agreeToTerms: string;
    shiftEligibility: string[];
    skills: string[];
    notifPreferences: string[];
    isAdmin?: boolean;
}

export interface AdminRegistrationResponse {
    message: string;
    requiresActivation: boolean;
}

export interface ActivateUserPayload {
    token: string;
}

export interface ActivateUserResponse {
    message: string;
}

interface RegistrationState {
    registering: boolean;
    registrationSuccess: boolean;
    error: string | null;
    sites: Site[];
    sitesLoading: boolean;
    validating: boolean;
    validationError: string | null;
    validationResponse: ValidateUserResponse | null;
    adminRegistrationResponse: AdminRegistrationResponse | null;
    activating: boolean;
    activationSuccess: boolean;
    activationError: string | null;
    activationMessage: string | null;
    checkingSuperUser: boolean;
    superUserCheckResponse: CheckSuperUserResponse | null;
    superUserCheckError: string | null;
}

const initialState: RegistrationState = {
    registering: false,
    registrationSuccess: false,
    error: null,
    sites: [],
    sitesLoading: false,
    validating: false,
    validationError: null,
    validationResponse: null,
    adminRegistrationResponse: null,
    activating: false,
    activationSuccess: false,
    activationError: null,
    activationMessage: null,
    checkingSuperUser: false,
    superUserCheckResponse: null,
    superUserCheckError: null,
};

// ========================================
// HELPERS
// ========================================
/**
 * Builds the common request body for user registration
 * Used by both createNewUser and createAdminUser thunks
 */
const buildRegistrationRequestBody = (payload: CreateUserPayload) => ({
    siteCd: payload.siteCd,
    workdayId: payload.workdayId,
    firstName: payload.firstName,
    lastName: payload.lastName,
    passwordHash: payload.passwordHash,
    status: "PND",
    team: payload.team,
    email: payload.email,
    shiftEligibility: payload.shiftEligibility || [],
    lstUpdtId: payload.workdayId,
    notes: payload.notes || null,
    phoneNbr: payload.phoneNbr || null,
    agreeToTerms: payload.agreeToTerms ? 'Y' : 'N',
    skills: payload.skills || [],
    notifPreferences: payload.notifPreferences || [],
});

/**
 * Generic error handler for async thunks
 */
const handleThunkError = (error: any, defaultMessage: string): string => {
    return error?.response?.data?.message || error?.message || defaultMessage;
};

// ========================================
// ASYNC THUNKS
// ========================================
export const fetchSites = createAsyncThunk(
    'registration/fetchSites',
    async (_, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(ENDPOINTS.GET_SITES);
            return response;
        } catch (error: any) {
            return rejectWithValue(handleThunkError(error, 'Failed to fetch sites'));
        }
    }
);

export const checkSuperUser = createAsyncThunk(
    'registration/checkSuperUser',
    async (payload: CheckSuperUserPayload, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(
                `${ENDPOINTS.CHECK_SUPER_USER}?workdayId=${payload.workdayId}&firstName=${encodeURIComponent(payload.firstName)}&lastName=${encodeURIComponent(payload.lastName)}`
            );
            return response as CheckSuperUserResponse;
        } catch (error: any) {
            return rejectWithValue(
                handleThunkError(error, 'Failed to check super user status')
            );
        }
    }
);

export const validateUser = createAsyncThunk(
    'registration/validateUser',
    async (payload: ValidateUserPayload, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(
                `${ENDPOINTS.VALIDATE_USER}?workdayId=${payload.workdayId}&firstName=${encodeURIComponent(payload.firstName)}&lastName=${encodeURIComponent(payload.lastName)}&siteCd=${payload.siteCd}`
            );

            if (response?.isActive === false) {
                return rejectWithValue(
                    response.message || 'User is not an active employee in the system'
                );
            }

            return response as ValidateUserResponse;
        } catch (error: any) {
            return rejectWithValue(
                handleThunkError(error, 'User validation failed - not an Active employee with Ace Hardware. Please check entered information.')
            );
        }
    }
);

export const createNewUser = createAsyncThunk(
    'registration/createNewUser',
    async (payload: CreateUserPayload, { rejectWithValue }) => {
        try {
            const requestBody = buildRegistrationRequestBody(payload);
            const response = await API_POST(ENDPOINTS.CREATE_NEW_USER, requestBody);
            return response;
        } catch (error: any) {
            return rejectWithValue(handleThunkError(error, 'Failed to register user'));
        }
    }
);

export const createAdminUser = createAsyncThunk(
    'registration/createAdminUser',
    async (payload: CreateUserPayload, { rejectWithValue }) => {
        try {
            const requestBody = buildRegistrationRequestBody(payload);
            const response = await API_POST(ENDPOINTS.REGISTER_ADMIN, requestBody);
            return response as AdminRegistrationResponse;
        } catch (error: any) {
            return rejectWithValue(handleThunkError(error, 'Failed to register admin user'));
        }
    }
);

export const activateUser = createAsyncThunk(
    'registration/activateUser',
    async (payload: ActivateUserPayload, { rejectWithValue }) => {
        try {
            const response = await API_POST(ENDPOINTS.ACTIVATE_USER, { token: payload.token });
            return response as ActivateUserResponse;
        } catch (error: any) {
            return rejectWithValue(handleThunkError(error, 'Failed to activate account'));
        }
    }
);

// ========================================
// SLICE
// ========================================
const registrationSlice = createSlice({
    name: 'registration',
    initialState,
    reducers: {
        clearRegistrationState: (state) => {
            state.registering = false;
            state.registrationSuccess = false;
            state.error = null;
            state.validating = false;
            state.validationError = null;
            state.validationResponse = null;
            state.adminRegistrationResponse = null;
            state.checkingSuperUser = false;
            state.superUserCheckResponse = null;
            state.superUserCheckError = null;
        },
        clearRegistrationError: (state) => {
            state.error = null;
        },
        clearValidationError: (state) => {
            state.validationError = null;
        },
        clearActivationState: (state) => {
            state.activating = false;
            state.activationSuccess = false;
            state.activationError = null;
            state.activationMessage = null;
        },
        clearSuperUserCheck: (state) => {
            state.checkingSuperUser = false;
            state.superUserCheckResponse = null;
            state.superUserCheckError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSites.pending, (state) => {
                state.sitesLoading = true;
            })
            .addCase(fetchSites.fulfilled, (state, action) => {
                state.sitesLoading = false;
                state.sites = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchSites.rejected, (state) => {
                state.sitesLoading = false;
                state.sites = [];
            })
            .addCase(checkSuperUser.pending, (state) => {
                state.checkingSuperUser = true;
                state.superUserCheckError = null;
                state.superUserCheckResponse = null;
            })
            .addCase(checkSuperUser.fulfilled, (state, action) => {
                state.checkingSuperUser = false;
                state.superUserCheckError = null;
                state.superUserCheckResponse = action.payload;
            })
            .addCase(checkSuperUser.rejected, (state, action) => {
                state.checkingSuperUser = false;
                state.superUserCheckError = action.payload as string;
                state.superUserCheckResponse = null;
            })
            .addCase(validateUser.pending, (state) => {
                state.validating = true;
                state.validationError = null;
                state.validationResponse = null;
            })
            .addCase(validateUser.fulfilled, (state, action) => {
                state.validating = false;
                state.validationError = null;
                state.validationResponse = action.payload;
            })
            .addCase(validateUser.rejected, (state, action) => {
                state.validating = false;
                state.validationError = action.payload as string;
                state.validationResponse = null;
            })
            .addCase(createNewUser.pending, (state) => {
                state.registering = true;
                state.registrationSuccess = false;
                state.error = null;
            })
            .addCase(createNewUser.fulfilled, (state) => {
                state.registering = false;
                state.registrationSuccess = true;
                state.error = null;
            })
            .addCase(createNewUser.rejected, (state, action) => {
                state.registering = false;
                state.registrationSuccess = false;
                state.error = action.payload as string;
            })
            .addCase(createAdminUser.pending, (state) => {
                state.registering = true;
                state.registrationSuccess = false;
                state.error = null;
                state.adminRegistrationResponse = null;
            })
            .addCase(createAdminUser.fulfilled, (state, action) => {
                state.registering = false;
                state.registrationSuccess = true;
                state.error = null;
                state.adminRegistrationResponse = action.payload;
            })
            .addCase(createAdminUser.rejected, (state, action) => {
                state.registering = false;
                state.registrationSuccess = false;
                state.error = action.payload as string;
                state.adminRegistrationResponse = null;
            })
            .addCase(activateUser.pending, (state) => {
                state.activating = true;
                state.activationSuccess = false;
                state.activationError = null;
                state.activationMessage = null;
            })
            .addCase(activateUser.fulfilled, (state, action) => {
                state.activating = false;
                state.activationSuccess = true;
                state.activationError = null;
                state.activationMessage = action.payload.message;
            })
            .addCase(activateUser.rejected, (state, action) => {
                state.activating = false;
                state.activationSuccess = false;
                state.activationError = action.payload as string;
                state.activationMessage = null;
            });
    },
});

export const { clearRegistrationState, clearRegistrationError, clearValidationError, clearActivationState, clearSuperUserCheck } = registrationSlice.actions;
export default registrationSlice.reducer;
